(() => {
  if (window.CosmeauV6PdpGallerySwipe) return;

  const configurations = [
    {
      root: '.cv6-default-gallery',
      surface: '.cv6-default-gallery__main',
      thumbs: '[data-gallery-thumb]',
      nativeScroll: true
    },
    {
      root: '.cv6-g6__gallery',
      surface: '.cv6-g6__main-media',
      thumbs: '[data-cv6-gallery-thumb]'
    },
    {
      root: '.v6gp__gallery',
      surface: '[data-v6gp-main]',
      thumbs: '[data-v6gp-thumb]'
    },
    {
      root: '.cv6-gallery',
      surface: '.cv6-gallery__stage',
      thumbs: '[data-cv6-gallery-thumb]'
    }
  ];

  const interactiveSelector = 'button, a, input, select, textarea, video, audio, iframe, model-viewer, [role="button"]';

  const activeIndex = thumbs => {
    const index = thumbs.findIndex(thumb =>
      thumb.classList.contains('is-active') ||
      thumb.getAttribute('aria-pressed') === 'true' ||
      thumb.getAttribute('aria-selected') === 'true'
    );
    return index < 0 ? 0 : index;
  };

  const bindHalfTap = (surface, root, go) => {
    if (surface.dataset.cv6HalfTapReady === '1') return;
    surface.dataset.cv6HalfTapReady = '1';
    if (window.matchMedia?.('(pointer: fine)').matches) surface.style.cursor = 'pointer';

    let press = null;
    let suppressClickUntil = 0;

    surface.addEventListener('pointerdown', event => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest(interactiveSelector)) return;
      press = { id: event.pointerId, x: event.clientX, y: event.clientY };
    });

    surface.addEventListener('pointermove', event => {
      if (!press || event.pointerId !== press.id) return;
      const dx = event.clientX - press.x;
      const dy = event.clientY - press.y;
      if (Math.hypot(dx, dy) > 12) suppressClickUntil = performance.now() + 500;
    }, { passive: true });

    surface.addEventListener('pointerup', event => {
      if (!press || event.pointerId !== press.id) return;
      const dx = event.clientX - press.x;
      const dy = event.clientY - press.y;
      if (Math.hypot(dx, dy) > 12) suppressClickUntil = performance.now() + 500;
      press = null;
    });

    surface.addEventListener('pointercancel', () => {
      press = null;
      suppressClickUntil = performance.now() + 500;
    });

    surface.addEventListener('click', event => {
      if (event.target.closest(interactiveSelector)) return;
      if (performance.now() < suppressClickUntil) return;
      if (event.detail === 0 && event.clientX === 0) return;
      const rect = surface.getBoundingClientRect();
      const direction = event.clientX < rect.left + rect.width / 2 ? -1 : 1;
      const move = go(direction);
      if (!move) return;
      root.dispatchEvent(new CustomEvent('cv6:gallery-half-tap', {
        detail: { ...move, direction }
      }));
    });
  };

  const lockNativeThumbSelection = (surface, thumbs, targetIndex) => {
    const token = String((Number(surface.dataset.cv6ThumbLockToken) || 0) + 1);
    surface.dataset.cv6ThumbLockToken = token;
    const enforce = () => {
      if (surface.dataset.cv6ThumbLockToken !== token) return;
      thumbs.forEach((thumb, index) => {
        const active = index === targetIndex;
        if (thumb.classList.contains('is-active') !== active) thumb.classList.toggle('is-active', active);
        if (thumb.getAttribute('aria-pressed') !== String(active)) thumb.setAttribute('aria-pressed', String(active));
      });
    };
    const observer = new MutationObserver(enforce);
    thumbs.forEach(thumb => observer.observe(thumb, {
      attributes: true,
      attributeFilter: ['class', 'aria-pressed']
    }));
    enforce();
    window.setTimeout(() => {
      observer.disconnect();
      if (surface.dataset.cv6ThumbLockToken === token) delete surface.dataset.cv6ThumbLockToken;
    }, 1000);
  };

  const bind = (root, configuration) => {
    const surface = root.querySelector(configuration.surface);
    const thumbs = [...root.querySelectorAll(configuration.thumbs)];
    if (!surface || thumbs.length < 2 || surface.dataset.cv6SwipeReady === '1') return;

    surface.dataset.cv6SwipeReady = '1';
    surface.setAttribute('aria-label', surface.getAttribute('aria-label') || 'Productafbeeldingen, veeg of tik links en rechts');
    surface.querySelectorAll('img').forEach(image => image.setAttribute('draggable', 'false'));
    const go = direction => {
      const current = activeIndex(thumbs);
      const next = Math.max(0, Math.min(thumbs.length - 1, current + direction));
      if (next === current) return null;
      if (configuration.nativeScroll) lockNativeThumbSelection(surface, thumbs, next);
      thumbs[next].click();
      if (!configuration.nativeScroll) {
        thumbs[next].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
      root.dispatchEvent(new CustomEvent('cv6:gallery-swipe', { detail: { from: current, to: next } }));
      return { from: current, to: next };
    };

    bindHalfTap(surface, root, go);

    if (configuration.nativeScroll) {
      surface.style.touchAction = 'pan-x pan-y pinch-zoom';
      return;
    }

    surface.style.touchAction = 'pan-y';
    surface.style.userSelect = 'none';

    let gesture = null;
    const reset = () => { gesture = null; };

    surface.addEventListener('pointerdown', event => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest(interactiveSelector)) return;
      gesture = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        lastX: event.clientX,
        lastY: event.clientY,
        time: performance.now()
      };
      surface.setPointerCapture?.(event.pointerId);
    });

    surface.addEventListener('pointermove', event => {
      if (!gesture || event.pointerId !== gesture.id) return;
      gesture.lastX = event.clientX;
      gesture.lastY = event.clientY;
      const dx = gesture.lastX - gesture.x;
      const dy = gesture.lastY - gesture.y;
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) event.preventDefault();
    }, { passive: false });

    surface.addEventListener('pointerup', event => {
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      const elapsed = performance.now() - gesture.time;
      reset();
      if (Math.abs(dx) < 42 || Math.abs(dx) <= Math.abs(dy) * 1.15 || elapsed > 1200) return;
      go(dx < 0 ? 1 : -1);
    });

    surface.addEventListener('pointercancel', reset);
    surface.addEventListener('dragstart', event => event.preventDefault());

  };

  const init = scope => {
    configurations.forEach(configuration => {
      if (scope.matches?.(configuration.root)) bind(scope, configuration);
      scope.querySelectorAll?.(configuration.root).forEach(root => bind(root, configuration));
    });
  };

  const boot = () => init(document);
  window.CosmeauV6PdpGallerySwipe = { init: boot };
  document.addEventListener('shopify:section:load', event => init(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
